<script setup lang="ts">
import { usePagination } from "~/composables/paginations/usePagination";
import ToggleButton from "~/components/Indexs/PaginationButton/ToggleButton.vue";
import ToggleButtonPagination from "~/components/Indexs/PaginationButton/ToggleButtonPagination.vue";
import DisabledTooltipWrapper from "~/components/Common/DisabledTooltipWrapper.vue";

const props = withDefaults(
  defineProps<{
    nbItems: number;
    itemPerPage: number;
    paginationKey?: string;
    disabled?: boolean;
    disabledTooltipMessage?: string;
  }>(),
  {
    disabled: false,
  },
);
const emit = defineEmits(["navigate"]);
const paginate = usePagination(props.paginationKey);
const currPage = computed(() => {
  return Number(paginate.paginateOptions.value.offset) / props.itemPerPage;
});
const numberItems = computed(() => {
  return [50, 100, 200];
});

const ceiling = computed(() => {
  return Math.ceil(props.nbItems / props.itemPerPage);
});

const navigate = (index: number) => {
  if (index >= 0 && index < ceiling.value) {
    // curr_page.value = index
    emit("navigate", {
      offset: index * props.itemPerPage,
      limit: props.itemPerPage,
    });
  }
};

const paginationItems = computed(() => {
  const offset = currPage.value * props.itemPerPage;
  let padding = (currPage.value + 1) * props.itemPerPage;

  if (padding > props.nbItems) {
    padding = props.nbItems;
  }

  return `${offset + (padding ? 1 : 0)} à ${padding} sur ${props.nbItems}`;
});

const getRangePagination = computed(() => {
  const pagination: number[] = [];
  const SIBLINGS = 2; // WORKS NOW FOR 2 SIBLINGS, MAKE IT SCALABLE LATER

  let leftPadding = currPage.value < SIBLINGS ? currPage.value : SIBLINGS;
  if (leftPadding > ceiling.value / 2) leftPadding = 0;

  let rightPadding =
    currPage.value + SIBLINGS >= ceiling.value ? Math.abs(ceiling.value - (currPage.value + SIBLINGS + 1)) : 0;
  if (currPage.value + SIBLINGS > ceiling.value / 2) rightPadding = 0;

  const totalPadding = leftPadding - rightPadding;

  for (
    let i = currPage.value - totalPadding;
    i < currPage.value + SIBLINGS * 2 + 1 - totalPadding && i < ceiling.value;
    i++
  ) {
    pagination.push(i);
  }
  return pagination;
});

function changeNumberItemsPerPage(number: number) {
  emit("navigate", {
    offset: 0 * props.itemPerPage,
    limit: number,
  });
}
</script>

<template>
  <div class="flex justify-between items-center">
    <DisabledTooltipWrapper
      :disabled="props.disabled"
      tooltip-position="top-mid"
      :tooltip-message="props.disabledTooltipMessage"
    >
      <template #button>
        <div :inert="props.disabled" class="flex flex-row overflow-x-hidden tight_regular gap-2">
          <span class="pagination_text">{{ paginationItems }}</span>
          <div>
            <ToggleButtonPagination
              :items="ceiling"
              :selected="props.itemPerPage"
              :current-page="currPage"
              @click="navigate"
            ></ToggleButtonPagination>
          </div>
        </div>
      </template>
    </DisabledTooltipWrapper>
    <DisabledTooltipWrapper
      :disabled="props.disabled"
      tooltip-position="top-mid"
      :tooltip-message="props.disabledTooltipMessage"
    >
      <template #button>
        <div :inert="props.disabled" class="flex pagination_text gap-2 cursor-pointer">
          <ToggleButton
            :items="numberItems"
            :selected="props.itemPerPage"
            @click="changeNumberItemsPerPage"
          ></ToggleButton>
          <div>éléments par page</div>
        </div>
      </template>
    </DisabledTooltipWrapper>
  </div>
</template>

<style lang="scss">
@use "assets/scss/texts";

.pagination_text {
  @extend .meta;
  @apply flex items-center w-fit;
  color: var(--text-secondary-color);
}

.pagination_slot {
  @apply flex justify-center items-center cursor-pointer w-[24px] h-[24px];
}

.pagination_nb {
  @apply flex flex-col justify-center items-center p-[8px];

  border: 1px solid transparent;
  border-radius: 4px;

  color: var(--text-secondary-color);

  &[data-current="true"] {
    border: 1px solid var(--primary-blue-color);
    border-radius: 4px;
    color: var(--primary-blue-color);
  }
}
</style>
